import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Cart, CartItem } from '../types/cart';
import { Product } from '../types/product';

interface CartStore extends Cart {
  addItem: (product: Product, quantity: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  getItemCount: () => number;
}

const calculateTotals = (items: CartItem[]): Pick<Cart, 'subtotal' | 'itemCount'> => {
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  return { subtotal, itemCount };
};

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      subtotal: 0,
      shipping: 0,
      tax: 0,
      total: 0,
      itemCount: 0,

      addItem: (product: Product, quantity: number) => {
        set((state) => {
          const existingItem = state.items.find(item => item.productId === product.id);
          
          let newItems: CartItem[];
          if (existingItem) {
            newItems = state.items.map(item =>
              item.productId === product.id
                ? { ...item, quantity: Math.min(item.quantity + quantity, item.maxQuantity) }
                : item
            );
          } else {
            newItems = [...state.items, {
              id: `${product.id}-${Date.now()}`,
              productId: product.id,
              name: product.name,
              price: product.price,
              quantity: Math.min(quantity, product.stockQuantity),
              unit: product.unit,
              image: product.images[0],
              maxQuantity: product.stockQuantity,
            }];
          }

          const { subtotal, itemCount } = calculateTotals(newItems);
          const shipping = subtotal > 50 ? 0 : 5.99;
          const tax = subtotal * 0.08;
          const total = subtotal + shipping + tax;

          return { items: newItems, subtotal, shipping, tax, total, itemCount };
        });
      },

      removeItem: (productId: string) => {
        set((state) => {
          const newItems = state.items.filter(item => item.productId !== productId);
          const { subtotal, itemCount } = calculateTotals(newItems);
          const shipping = subtotal > 50 ? 0 : 5.99;
          const tax = subtotal * 0.08;
          const total = subtotal + shipping + tax;

          return { items: newItems, subtotal, shipping, tax, total, itemCount };
        });
      },

      updateQuantity: (productId: string, quantity: number) => {
        set((state) => {
          if (quantity <= 0) {
            const newItems = state.items.filter(item => item.productId !== productId);
            const { subtotal, itemCount } = calculateTotals(newItems);
            const shipping = subtotal > 50 ? 0 : 5.99;
            const tax = subtotal * 0.08;
            const total = subtotal + shipping + tax;

            return { items: newItems, subtotal, shipping, tax, total, itemCount };
          }

          const newItems = state.items.map(item =>
            item.productId === productId
              ? { ...item, quantity: Math.min(quantity, item.maxQuantity) }
              : item
          );

          const { subtotal, itemCount } = calculateTotals(newItems);
          const shipping = subtotal > 50 ? 0 : 5.99;
          const tax = subtotal * 0.08;
          const total = subtotal + shipping + tax;

          return { items: newItems, subtotal, shipping, tax, total, itemCount };
        });
      },

      clearCart: () => {
        set({
          items: [],
          subtotal: 0,
          shipping: 0,
          tax: 0,
          total: 0,
          itemCount: 0,
        });
      },

      getItemCount: () => {
        return get().itemCount;
      },
    }),
    {
      name: 'angel-cite-cart',
    }
  )
);

